import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { assertAuthApiOk } from '../../../utils/authApiErrors.js';
import HubAuthShell from './HubAuthShell.jsx';

const SIGNUP_SUCCESS_MESSAGE = 'メールアドレスの確認が完了しました。登録の際に開いていた画面にお戻りください。確認が完了次第、自動的にログインし、サービスをご利用いただけます。';

const cacheKeyForConfirm = (token, clinicId) => `synapse.verify-email:${token}:${clinicId}`;
/** Strict Mode 二重マウントでも同一 token の confirm を1回にまとめる */
const confirmInflight = new Map();

function confirmEmailOnce(token, clinicId) {
  const inflightKey = `${token}:${clinicId}`;
  if (confirmInflight.has(inflightKey)) return confirmInflight.get(inflightKey);

  const cacheKey = cacheKeyForConfirm(token, clinicId);
  try {
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw);
      if (cached && typeof cached.ok === 'boolean') {
        return Promise.resolve(cached);
      }
    }
  } catch {
    // ignore
  }

  let settle;
  const promise = new Promise((resolve) => { settle = resolve; });
  confirmInflight.set(inflightKey, promise);

  (async () => {
    try {
      const response = await fetch(buildApiUrl('/api/auth/confirm-email'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, clinicId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        assertAuthApiOk(response, data, 'メールアドレスの確認に失敗しました。');
        throw new Error(data.message || 'メールアドレスの確認に失敗しました。');
      }
      const purpose = data.purpose === 'email-change' ? 'email-change' : 'signup';
      const result = {
        ok: true,
        purpose,
        message: data.message || (
          purpose === 'email-change'
            ? '新しいメールアドレスの確認が完了しました。クリニック設定へお進みください。'
            : SIGNUP_SUCCESS_MESSAGE
        ),
      };
      try { sessionStorage.setItem(cacheKey, JSON.stringify(result)); } catch { /* ignore */ }
      settle(result);
    } catch (err) {
      settle({
        ok: false,
        message: err.message || 'メールアドレスの確認に失敗しました。',
      });
    } finally {
      confirmInflight.delete(inflightKey);
    }
  })();

  return promise;
}

/**
 * メール確認リンク着地ページ（?token=&clinicId=）
 */
const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const clinicId = searchParams.get('clinicId');
  const [status, setStatus] = useState(token && clinicId ? 'loading' : 'invalid');
  const [message, setMessage] = useState(
    token && clinicId
      ? ''
      : '確認リンクが無効、または有効期限切れの可能性があります。お手数ですが、確認メールの再送をお試しください。'
  );
  const [purpose, setPurpose] = useState('signup');

  useEffect(() => {
    if (!token || !clinicId) return undefined;
    let cancelled = false;

    confirmEmailOnce(token, clinicId).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setPurpose(result.purpose === 'email-change' ? 'email-change' : 'signup');
        setMessage(result.message || (
          result.purpose === 'email-change'
            ? '新しいメールアドレスの確認が完了しました。クリニック設定へお進みください。'
            : SIGNUP_SUCCESS_MESSAGE
        ));
        setStatus('success');
      } else {
        setMessage(
          result.message
            || '確認リンクが無効、または有効期限切れの可能性があります。お手数ですが、確認メールの再送をお試しください。'
        );
        setStatus('error');
      }
    });

    return () => { cancelled = true; };
  }, [token, clinicId]);

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <>
          <div className="hub-auth-header">
            <h1 className="hub-auth-title">確認中です</h1>
            <p className="hub-auth-subtitle">メールアドレスを確認しています。しばらくお待ちください。</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner size="large" />
          </div>
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <div className="hub-auth-header">
            <div className="hub-auth-logo" style={{ background: 'var(--hub-success)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h1 className="hub-auth-title">確認が完了しました</h1>
            <p className="hub-auth-subtitle">{message}</p>
          </div>
          {purpose === 'email-change' ? (
            <Link to="/settings/clinic" className="hub-btn hub-btn-primary" style={{ width: '100%', textAlign: 'center' }}>
              クリニック設定へ
            </Link>
          ) : (
            <Link to="/login" className="hub-btn hub-btn-primary" style={{ width: '100%', textAlign: 'center' }}>
              ログインページへ進む
            </Link>
          )}
        </>
      );
    }

    return (
      <>
        <div className="hub-auth-header">
          <h1 className="hub-auth-title">リンクをご確認ください</h1>
          <p className="hub-auth-subtitle">
            {message || '確認リンクが無効、または有効期限切れの可能性があります。お手数ですが、確認メールの再送をお試しください。'}
          </p>
        </div>
        <Link to="/verify-email/pending" className="hub-btn hub-btn-primary" style={{ width: '100%', textAlign: 'center' }}>
          確認メールを再送する
        </Link>
        <div className="hub-auth-footer">
          <p><Link to="/login">ログインページに戻る</Link></p>
        </div>
      </>
    );
  };

  return (
    <HubAuthShell>
      {renderBody()}
    </HubAuthShell>
  );
};

export default VerifyEmailPage;
